import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Grid, styled } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import PropTypes from 'prop-types';

import { ReactComponent as CentralisedInventory } from '../../../assets/VaultLoginPage/CentralisedInventory.svg';
import { ReactComponent as Attestation } from '../../../assets/VaultLoginPage/Attestation.svg';
import { ReactComponent as ModelValidation } from '../../../assets/VaultLoginPage/ModelValidation.svg';
import { ReactComponent as VaultIcon } from '../../../assets/VaultLoginPage/VaultLogo.svg';

const EmblaCarousel = styled(Box)({
  overflow: 'hidden',
  width: '100%',
  height: '100%',
});

const EmblaContainer = styled(Box)({
  display: 'flex',
});

const EmblaSlide = styled(Box)({
  flex: '0 0 100%',
  minWidth: 0,
});

const EmblaIndicators = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '10px',
  gap: '5px',
});

const EmblaIndicator = styled('button')(({ theme, active }) => ({
  width: '11px',
  height: '11px',
  minWidth: '11px',
  minHeight: '11px',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  margin: 0,
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  transition: 'background-color 0.5s ease',
}));

const carouselItemsList = [
  {
    id: 1,
    title: 'Centralized Inventory',
    info: 'On-demand access to model meta-data,\n records and model history',
    mainImage: <CentralisedInventory style={{ height: '70%', width: '70%' }} />,
  },
  {
    id: 2,
    title: 'Attestation and Governance',
    info: 'Attest and review models, both periodically \nand based on specific-request',
    mainImage: <Attestation style={{ height: '70%', width: '70%' }} />,
  },
  {
    id: 3,
    title: 'Model Validation',
    info: 'Initiate and manage Model \nValidation Workflows',
    mainImage: <ModelValidation style={{ height: '70%', width: '70%' }} />,
  },
];

function CarouselItem({ title, mainImage, info }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifySelf="center"
    >
      <Box mb={3}>
        <Typography
          sx={{ whiteSpace: 'pre-line' }}
          color="primary"
          variant="h1"
          align="center"
        >
          {title}
        </Typography>
      </Box>
      <Box mb={3}>
        <Typography
          sx={{ whiteSpace: 'pre-line' }}
          color="primary"
          variant="h5"
          align="center"
        >
          {info}
        </Typography>
      </Box>
      <Box>{mainImage}</Box>
    </Box>
  );
}

function VaultLoginGraphic() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, skipSnaps: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (index) => {
      if (embla) embla.scrollTo(index);
    },
    [embla]
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;

    embla.on('select', onSelect);

    // Auto play functionality
    const autoPlayInterval = setInterval(() => {
      if (embla.canScrollNext()) {
        embla.scrollNext();
      } else {
        embla.scrollTo(0);
      }
    }, 5000);

    return () => {
      embla.off('select', onSelect);
      clearInterval(autoPlayInterval);
    };
  }, [embla, onSelect]);

  return (
    <Grid item container xs={12} sm={12} md={6} px={8} py={5}>
      <Box>
        <VaultIcon height="50px" width="120px" />
      </Box>
      <Grid
        item
        xs={12}
        flexDirection="column"
        alignItems="center"
        mx="auto"
        sx={{
          mt: {
            xl: 'auto',
          },
          my: {
            xl: 'auto',
          },
        }}
      >
        <EmblaCarousel ref={emblaRef}>
          <EmblaContainer>
            {carouselItemsList.map((item) => (
              <EmblaSlide key={item.id}>
                <CarouselItem
                  title={item.title}
                  info={item.info}
                  mainImage={item.mainImage}
                />
              </EmblaSlide>
            ))}
          </EmblaContainer>
        </EmblaCarousel>

        <EmblaIndicators>
          {carouselItemsList.map((item, index) => (
            <EmblaIndicator
              key={`indicator-${item.id}`}
              active={index === selectedIndex}
              onClick={() => scrollTo(index)}
            />
          ))}
        </EmblaIndicators>
      </Grid>
    </Grid>
  );
}

CarouselItem.propTypes = {
  title: PropTypes.string.isRequired,
  mainImage: PropTypes.element.isRequired,
  info: PropTypes.string.isRequired,
};

export default VaultLoginGraphic;
